import { useEffect, useRef } from "react";
import styled from "styled-components";

/*
 * Pieces shared by the two forms that post to the contact-form Lambda
 * (pages/public/Contact.js and fragments/SingleProperty.js).
 */

export const FormError = styled.p`
    color: #b3261e;
    font-size: 0.9rem;
    margin: 0.75rem 0 0;
`;

/*
 * A field no visitor can see or reach. Bots that fill in every input fill this
 * in too, and the server discards the message without telling them. Off-screen
 * rather than display:none, which many bots skip. `fax` because browsers do not
 * autofill it, so a real visitor cannot trip it by accident.
 */
export function HoneypotField({ value, onChange }) {
    return (
        <div
            aria-hidden="true"
            style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}
        >
            <label>
                Leave this empty
                <input type="text" name="fax" tabIndex={-1} autoComplete="off" value={value} onChange={(e) => onChange(e.target.value)} />
            </label>
        </div>
    );
}

/*
 * Cloudflare Turnstile. INERT until REACT_APP_TURNSTILE_SITEKEY is set when the
 * site is built - without a key nothing loads and nothing renders, so the form
 * behaves exactly as before. With a key, the server (TURNSTILE_SECRET on the
 * Lambda) refuses any message that does not carry a valid token.
 */
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITEKEY;
export const turnstileEnabled = Boolean(SITE_KEY);

let scriptPromise = null;
function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (!scriptPromise) {
        scriptPromise = new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
            script.async = true;
            script.defer = true;
            script.onload = () => resolve(window.turnstile);
            script.onerror = () => {
                scriptPromise = null;
                reject(new Error("turnstile failed to load"));
            };
            document.head.appendChild(script);
        });
    }
    return scriptPromise;
}

/* `resetKey`: change it to ask for a fresh challenge (a token is single-use). */
export function TurnstileWidget({ onToken, resetKey }) {
    const box = useRef(null);
    const widgetId = useRef(null);

    useEffect(() => {
        if (!SITE_KEY) return undefined;
        let cancelled = false;
        loadTurnstile()
            .then((turnstile) => {
                if (cancelled || !box.current) return;
                widgetId.current = turnstile.render(box.current, {
                    sitekey: SITE_KEY,
                    callback: (token) => onToken(token),
                    "expired-callback": () => onToken(""),
                    "error-callback": () => onToken(""),
                });
            })
            .catch(() => onToken(""));
        return () => {
            cancelled = true;
            if (widgetId.current !== null && window.turnstile) window.turnstile.remove(widgetId.current);
            widgetId.current = null;
        };
    }, [onToken]);

    useEffect(() => {
        if (widgetId.current !== null && window.turnstile) {
            window.turnstile.reset(widgetId.current);
            onToken("");
        }
    }, [resetKey, onToken]);

    if (!SITE_KEY) return null;
    return <div ref={box} style={{ margin: "0.75rem 0" }} />;
}
